import { Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import "./style.scss";
import { BackSvgPPP, GlobeIconSvgPPP, SplitIconSvgPPP } from "../svgImages";
import { IPaymentPayload, IPriceData } from "../..";
import SnackbarContext from "../../../../context/snackbar.context";

interface IPricePaymentCard {
  priceData: IPriceData;
  discountDescription: string;
  partialOptions: { partial_allowed: boolean; partial_min_pct: number };
  splitPaymentOptions: { split_allowed: boolean; split_pct: number; min_payment_in_each_split: number };
  bookingData: any;
  quotationActive: boolean;
  editAccess: boolean;
  paymentPayload: IPaymentPayload;
  setPaymentPayload: Dispatch<SetStateAction<IPaymentPayload>>;
  paymentGoAhead: boolean;
  international: boolean;
}

const PricePaymentCard: React.FC<IPricePaymentCard> = (props) => {
  const snackbarCtx = useContext(SnackbarContext);
  const triggerSnackbar = () =>
    snackbarCtx.toggleSnackbar({
      message: "Please fill your billing details first.",
      type: "FAILURE",
      visible: true,
    });

  const { priceData, discountDescription, partialOptions, splitPaymentOptions, bookingData, quotationActive, editAccess, paymentPayload, setPaymentPayload, paymentGoAhead, international } = props;
  
  const [priceTrigger, setPriceTrigger] = useState<boolean>(true);
  const [paymentTrigger, setPaymentTrigger] = useState<boolean>(false);

  const [paymentType, setPaymentType] = useState<string>(""); // full_payment || partial_payment

  const [singlePaymentTrigger, setSinglePaymentTrigger] = useState<boolean>(false);
  const [splitPaymentTrigger, setSplitPaymentTrigger] = useState<boolean>(false);
  const [splitPaymentType, setSplitPaymentType] = useState<string>("");

  const [customAmount, setCustomAmount] = useState<number | null>(null);

  const handlePaymentTypeBtnClick = (paymentTypeValue: string) => {
    handleResetPayment();
    paymentTypeValue === paymentType ? setPaymentType("") : setPaymentType(paymentTypeValue)
  }

  const handleSinglePaymentClick = async () => {
    if (!paymentGoAhead) {
      triggerSnackbar();
      return;
    }
    if (paymentPayload.paymentType === "full_payment" || (!!bookingData?.total && paymentPayload.amount === Number((Number(bookingData?.total) - Number(bookingData?.amountPaid)).toFixed()))) {
      handleResetPayment();
    } else if (paymentGoAhead) {
      setSinglePaymentTrigger(true);
      setSplitPaymentTrigger(false);
      setSplitPaymentType("");
      let payload: any;
      payload = {
        ...paymentPayload,
        paymentType: `${!!bookingData?.splitPayment ? 'split_payment' : 'full_payment'}`,
        amount: Number(
          (!bookingData?.total
            ? priceData?.total - priceData?.discount
            : Number(bookingData?.total) - Number(bookingData?.amountPaid)
          ).toFixed()
        ),
      };
      delete payload.paymentToken;
      setPaymentPayload(payload);
    }
  };

  const handleSplitPaymentClick = () => {
    if (!paymentGoAhead) {
      triggerSnackbar();
      return;
    }
    if ((!!bookingData?.total && paymentPayload.amount > 0 && paymentPayload.amount < Number((Number(bookingData?.total) - Number(bookingData?.amountPaid)).toFixed())) || (!bookingData?.total && ['partial_payment', 'split_payment'].includes(paymentPayload.paymentType))) {
      handleResetPayment();
    } else {
      setSinglePaymentTrigger(false);
      setSplitPaymentTrigger(true);
      !bookingData?.amountPaid ? handleSplitPaymentType("optionA") : handleSplitPaymentType("optionB");
    }
  };

  const handleSplitPaymentType = async (option: string) => {
    if (!paymentGoAhead) {
      triggerSnackbar();
      return;
    }
    if (splitPaymentType === option) {
      setSplitPaymentType("");
      setPaymentPayload({
        ...paymentPayload,
        amount: 0,
        paymentToken: "",
      });
      return;
    }
    if (option === "optionA") {
      setSplitPaymentType(option);
      const amount = handleXPercentAmount();
      const payload: any = {
        ...paymentPayload,
        paymentType: (!!partialOptions?.partial_allowed && paymentType === 'partial_payment') ? 'partial_payment' : 'split_payment',
        amount: amount,
      };
      delete payload.paymentToken;
      setPaymentPayload(payload);
    } else if (option === "optionB") {
      setSplitPaymentType(option);
      setCustomAmount(null);
      const payload: any = {
        ...paymentPayload,
        paymentType: (!!partialOptions?.partial_allowed && paymentType === 'partial_payment') ? 'partial_payment' : 'split_payment',
        amount: 0,
      };
      delete payload.paymentToken;
      setPaymentPayload(payload);
    }
  };

  const handleAmountEdit = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (!!paymentPayload.amount) setPaymentPayload(prevValues => ({ ...prevValues, amount: 0 }));

    if (!value) setCustomAmount(null);
    else if (Number(value) > Number((Number(bookingData?.total) - Number(bookingData?.amountPaid)).toFixed())){
      setCustomAmount(Math.floor((Number(bookingData?.total) - Number(bookingData?.amountPaid))));
      setPaymentPayload(prevValues => ({ ...prevValues, amount: Math.floor((Number(bookingData?.total) - Number(bookingData?.amountPaid))) }));
    }
    else setCustomAmount(Math.floor(Number(value)));

    if (!!value && Number(value) <= Number((Number(bookingData?.total) - Number(bookingData?.amountPaid)).toFixed()) && Number(value) >= splitPaymentOptions?.min_payment_in_each_split)
      setPaymentPayload(prevValues => ({ ...prevValues, amount: Math.floor(Number(value)) }));
  };

  const handleResetPayment = () => {
    setPaymentPayload({...paymentPayload, paymentType: "", amount: 0, paymentToken: "" });
    setSinglePaymentTrigger(false);
    setSplitPaymentTrigger(false);
    setSplitPaymentType("");
  };

  useEffect(() => {handleResetPayment()}, [priceData.total]);

  const handleXPercentAmount = () => {
    if (international && paymentPayload?.tcsSlab) {
      return Number(((!!partialOptions?.partial_allowed ? (partialOptions?.partial_min_pct / 100) : (splitPaymentOptions?.split_pct / 100) ) * ((priceData?.total - priceData?.discount) + ((priceData?.total - priceData?.discount) * (paymentPayload.tcsSlab / 100)))).toFixed());
    } else {
      return Number(((!!partialOptions?.partial_allowed ? (partialOptions?.partial_min_pct / 100) : (splitPaymentOptions?.split_pct / 100) ) * (priceData?.total - priceData?.discount)).toFixed());
    }
  };

  const handleTotalAmount = (strike?: boolean) => {
    if (strike) {
      return international && paymentPayload?.tcsSlab
        ? Number((priceData?.total + (priceData?.total * (paymentPayload?.tcsSlab / 100))).toFixed())
        : Number((priceData?.total).toFixed());
    } else {
      return international && paymentPayload?.tcsSlab
        ? Number(((priceData?.total - priceData?.discount) + ((priceData?.total - priceData?.discount) * (paymentPayload?.tcsSlab / 100))).toFixed())
        : Number((priceData?.total - priceData?.discount).toFixed());
    }
  };

  return (
    <div className="price-payment-card-wrapper">
      <div
        className="price-details-header"
        onClick={() => setPriceTrigger((prev) => !prev)}
      >
        <span>Price Details</span>
        <span className={`${priceTrigger ? "open" : "close"}`}>
          <BackSvgPPP fill="black" height={14} />
        </span>
      </div>
      {priceTrigger && (
        <div className="price-detail-row-wrapper">
          <div className="price-detail-row-wrapper-cabins">
            {!!priceData?.cabin_names?.length &&
              priceData?.cabin_names?.map(
                (cabinName: string, index: number) => {
                  return (
                    <div key={index} className="common-row-wrapper">
                      <span className="cabin-fare-details">{cabinName}</span>
                      <span className="cabin-fare-details">{`₹ ${new Intl.NumberFormat(
                        "en-IN"
                      ).format(
                        Number((priceData?.cabin_fares[index]).toFixed(2))
                      )}`}</span>
                    </div>
                  );
                }
              )}
            {!!priceData?.service_tax_and_levies && (
              <div className="common-row-wrapper">
                <span className="service-tax-text">Service Tax & Levies</span>
                <span className="service-tax-amount">{`₹ ${new Intl.NumberFormat(
                  "en-IN"
                ).format(
                  Number((priceData?.service_tax_and_levies).toFixed(2))
                )}`}</span>
              </div>
            )}
            {/* {!!priceData?.port_charges && (
              <div className="common-row-wrapper">
                <span className="port-text">
                  (Port Charges + Gratuity)
                </span>
              </div>
            )} */}
            {!!priceData?.fuel_surcharge && (
              <div className="common-row-wrapper">
                <span className="service-tax-text">Fuel Surcharge</span>
                <span className="service-tax-amount">{`₹ ${new Intl.NumberFormat(
                  "en-IN"
                ).format(
                  Number((priceData?.fuel_surcharge).toFixed(2))
                )}`}</span>
              </div>
            )}
            {!!priceData?.discount && (
              <div className="common-row-wrapper">
                <span className="discount-text">Discount</span>
                <span className="discount-amount">{`-₹ ${new Intl.NumberFormat(
                  "en-IN"
                ).format(Number((priceData?.discount).toFixed(2)))}`}</span>
              </div>
            )}
            {!!priceData?.discount && (
              <div className="common-row-wrapper">
                <span className="discount-title">
                  ({!!discountDescription ? discountDescription : Object.keys(priceData?.discount_category).join(", ")})
                </span>
              </div>
            )}
            {/* <div className="common-row-wrapper coupon-code-wrapper">
              <span className="applied-coupon-text">Applied Coupon</span>
              <div>
                <span className="applied-coupon-code">SUMMER 20</span>
                <span className="remove-text">Remove</span>
              </div>
            </div> */}
          </div>
          <div className="price-detail-row-wrapper-ssrs">
            {!!priceData?.ssr_names?.length && (
              <span className="cabin-fare-details">
                Special Service Requests
              </span>
            )}
            {!!priceData?.ssr_names?.length &&
              priceData?.ssr_names?.map((ssrName: string, index: number) => {
                return (
                  <div key={index} className="common-row-wrapper">
                    <span className="ssr-fare-details">
                      {ssrName?.toLowerCase()}
                    </span>
                    <span className="ssr-fare-details">{`₹${new Intl.NumberFormat(
                      "en-IN"
                    ).format(
                      Number((priceData?.ssr_fares[index]).toFixed(2))
                    )}`}</span>
                  </div>
                );
              })}
            <div className="common-row-wrapper">
              <span className="gst-details">GST</span>
              <span className="gst-details">{`₹ ${new Intl.NumberFormat(
                "en-IN"
              ).format(Number((priceData?.gst).toFixed(2)))}`}</span>
            </div>

            {international && paymentPayload?.tcsSlab && (
              <div className="common-row-wrapper">
                <span className="service-tax-details">
                  TCS ({paymentPayload?.tcsSlab ? paymentPayload.tcsSlab : 0}%)
                </span>
                <span className="service-tax-details">{`₹ ${new Intl.NumberFormat(
                  "en-IN"
                ).format(
                  Number(
                    ((priceData?.total - priceData?.discount) * (paymentPayload.tcsSlab / 100)).toFixed(2)
                  )
                )}`}</span>
              </div>
            )}
          </div>
        </div>
      )}
      <div className={`price-detail-row-wrapper-total ${priceTrigger ? "border-top" : ""} 
        ${bookingData?.status === "CONFIRMED" || !quotationActive ? "drop-bb" : ""}`}>
        <span className="total-amount-text">Total Amount</span>
        <div className="total-amount-price-wrapper">
          {!!priceData?.discount && (
            <div className="strikethrough-price-wrapper">
              <span className="striked-price">
                {`₹ ${new Intl.NumberFormat("en-IN").format(
                  handleTotalAmount(true)
                )}`}
              </span>
              <div className="strike"></div>
            </div>
          )}
          <span className="total-amount-price">
            {bookingData?.status === "CONFIRMED" && (
              <span className="paid-text">Paid</span>
            )}
            {`₹ ${new Intl.NumberFormat("en-IN").format(handleTotalAmount())}`}
          </span>
        </div>
      </div>
      {!editAccess &&
        bookingData?.status !== "CONFIRMED" &&
        !!bookingData?.total && (
          <div
            className={`booking-details-wrapper ${!editAccess ? "" : "no-bb"}`}
          >
            <div>
              <span className="left-text-amount">Amount Paid</span>
              <span className="right-text-amount">
                {`₹ ${new Intl.NumberFormat("en-IN").format(
                  Number(Number(bookingData?.amountPaid).toFixed())
                )}`}
              </span>
            </div>
            <div>
              <span className="left-text-amount">Pending Amount</span>
              <span className="right-text-amount">
                {`₹ ${new Intl.NumberFormat("en-IN").format(
                  Number((Number(bookingData?.total) - Number(bookingData?.amountPaid)).toFixed())
                )}`}
              </span>
            </div>
          </div>
        )}
      {(quotationActive || bookingData?.status === "RESERVED") && (
        <div
          className={`payment-options-header ${
            paymentTrigger ? "border-bottom" : ""
          }`}
          onClick={() =>
            paymentGoAhead
              ? setPaymentTrigger((prev) => !prev)
              : triggerSnackbar()
          }
        >
          <span>Select Payment Option</span>
          <span className={`${paymentTrigger ? "open" : "close"}`}>
            <BackSvgPPP fill="#008CFF" height={14} />
          </span>
        </div>
      )}
      {paymentTrigger && !!partialOptions?.partial_allowed && !bookingData?.amountPaid && (
        <div className={`payment-options-button-wrapper ${paymentType ? 'border-bottom' : ''}`}>
          <button className={paymentType === 'partial_payment' ? 'active' : ''} onClick={() => handlePaymentTypeBtnClick('partial_payment')}>Pay Partial Amount</button>
          <button className={paymentType === 'full_payment' ? 'active' : ''} onClick={() => handlePaymentTypeBtnClick('full_payment')}>Pay Full Amount</button>
        </div>)}
      {paymentTrigger && (!partialOptions?.partial_allowed || paymentType === 'full_payment' || !!bookingData?.amountPaid) && (
        <div
          className={`single-payment-wrapper ${
            singlePaymentTrigger ? "bg-single-payment" : ""
          }`}
          onClick={handleSinglePaymentClick}
        >
          <div className="body-wrapper">
            <GlobeIconSvgPPP />
            <div className="text-wrapper">
              <span className="main-text">Pay in single transaction</span>
              <span className="secondary-text">
                Pay{" "}
                {!bookingData?.total &&
                  `₹ ${new Intl.NumberFormat("en-IN").format(
                    handleTotalAmount()
                  )}`}
                {bookingData?.total &&
                  `₹ ${new Intl.NumberFormat("en-IN").format(
                    Number(bookingData?.total) - Number(bookingData?.amountPaid)
                  )}`}{" "}
                in one single transaction.
              </span>
            </div>
          </div>
        </div>
      )}
      {paymentTrigger && !!splitPaymentOptions?.split_allowed && (!partialOptions?.partial_allowed || paymentType === 'full_payment' || bookingData?.splitPayment) && (
        <div
        className={`split-payment-wrapper ${
          splitPaymentTrigger ? "bg-split-payment" : ""
          }`}
          onClick={handleSplitPaymentClick}
          >
          <div className="body-wrapper">
            <SplitIconSvgPPP />
            <div className="text-wrapper">
              <span className="main-text">Split the payment</span>
              <span className="secondary-text">
                Split into multiple transactions if you’ve limits in your card.
              </span>
            </div>
          </div>
          <span className={`${splitPaymentTrigger ? "open" : "close"}`}>
            <BackSvgPPP fill="#000" height={20} />
          </span>
        </div>
      )}
      {paymentTrigger && (splitPaymentTrigger || (!!partialOptions?.partial_allowed && paymentType === 'partial_payment')) && (
        <div className="split-payment-cards-wrapper">
          {!bookingData?.amountPaid && <div
            className={`split-payment-card-wrapper shadow ${splitPaymentType === "optionA" ? "active" : "inactive"}`}
            onClick={() => handleSplitPaymentType("optionA")}
          >
            <span className="main-text">Pay {(!!partialOptions?.partial_allowed && paymentType === 'partial_payment') ? 'Partial' : 'Split'} Amount</span>
            <span className="secondary-text">
              {(!!partialOptions?.partial_allowed && paymentType === 'partial_payment') ? partialOptions?.partial_min_pct?.toFixed() : splitPaymentOptions?.split_pct?.toFixed()}% of Total Amount
            </span>
            <span className="amount-text">{`₹ ${new Intl.NumberFormat(
              "en-IN"
            ).format(handleXPercentAmount())}`}</span>
          </div>}
          {!!bookingData?.amountPaid && <div
            className={`split-payment-card-wrapper shadow ${splitPaymentType === "optionB" ? "active" : "inactive"}`}
            onClick={() => handleSplitPaymentType("optionB")}
          >
            <span className="main-text">Pay Custom Amount</span>
            <span className="secondary-text">Balance Amount</span>
            <span className="amount-text">
              {`₹ ${new Intl.NumberFormat("en-IN").format(
                Number((Number(bookingData?.total) - Number(bookingData?.amountPaid)).toFixed())
              )}`}
            </span>
          </div>}
        </div>
      )}
      {paymentTrigger && (splitPaymentTrigger || bookingData?.splitPayment) && splitPaymentType === "optionB" && (
        <div className="custom-payment-input-wrapper">
          <div>
            <span>Enter Amount</span>
            <span>(Minimum Amount: {`₹ ${new Intl.NumberFormat("en-IN").format(splitPaymentOptions?.min_payment_in_each_split)}`})</span>
          </div>
          <div className="custom-payment-input-row">
            <input
              min="0"
              type="number"
              autoFocus
              name="custom-payment-input"
              id="custom-payment-input"
              value={customAmount || ''}
              onKeyDown={(e: React.KeyboardEvent) => {if(e.key === '-' || e.key === '.' || e.key === 'e' || e.key === 'E') e.preventDefault();}}
              onChange={handleAmountEdit}
            />
            <span className="rupee-abs">₹</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default PricePaymentCard;